'use client';

import { Divider } from '@common/components';
import SvgChevrondown from '@common/icon/SvgChevrondown';
import SvgDelete from '@common/icon/SvgDelete';
import SvgLogo from '@common/icon/SvgLogo';
import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import { motion } from 'framer-motion';
import Image from 'next/image';
import { Fragment, useEffect, useState } from 'react';

import { useCheckKakaobank } from '@/hooks/useCheckKakaobank';
import { getStaticCdnUrl } from '@/utils';

const BANNER_IMAGES = [
  {
    src: getStaticCdnUrl('/donation/guide-banner-1.png'),
    alt: '가이드 롤링 배너 이미지-1',
  },
  {
    src: getStaticCdnUrl('/donation/guide-banner-2.png'),
    alt: '가이드 롤링 배너 이미지-2',
  },
  {
    src: getStaticCdnUrl('/donation/guide-banner-3.png'),
    alt: '가이드 롤링 배너 이미지-3',
  },
  {
    src: getStaticCdnUrl('/donation/guide-banner-4.png'),
    alt: '가이드 롤링 배너 이미지-4',
  },
];

const TAX_DEDUCTION_INFO = [
  {
    label: '10만 원까지',
    rate: '100% 세액공제',
  },
  {
    label: '20만 원 이하',
    rate: '44% 세액공제',
  },
  {
    label: '그외 초과분',
    rate: '16.5% 세액공제',
  },
];

const BannerSection = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      align: 'center',
    },
    [Autoplay({ delay: 1500, stopOnInteraction: false })],
  );

  const [isReady, setIsReady] = useState(false);
  const isKakaobank = useCheckKakaobank();

  useEffect(() => {
    if (!emblaApi) return;

    const onInit = () => {
      setIsReady(true);
    };

    // 이미 초기화된 경우
    if (emblaApi.slideNodes().length > 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- embla 캐러셀 init 이벤트에서 준비 상태 동기화
      setIsReady(true);
    }

    emblaApi.on('init', onInit);

    return () => {
      emblaApi.off('init', onInit);
    };
  }, [emblaApi]);

  return (
    <section className="mt-60 flex flex-col items-center gap-112 bg-Gray-white mobile:gap-56">
      {/* 제목 영역 */}
      <div className="flex flex-col items-center gap-40 text-center mobile:gap-20">
        <div className="flex items-center gap-9">
          <SvgLogo className="h-38 min-w-112 mobile:h-19 mobile:min-w-60" />
          {!isKakaobank && (
            <>
              <SvgDelete className="size-28 mobile-sm:size-14" />
              <div className="relative h-46 w-163 mobile:h-20 mobile:w-80">
                <Image
                  src={getStaticCdnUrl('agency-logo-mois.png')}
                  alt="행정안전부 로고"
                  fill
                  loading="lazy"
                  sizes="(max-width: 850px) 80px, 163px"
                  className="object-contain"
                />
              </div>
            </>
          )}
        </div>
        <p className="font-800 text-Gray-black typo-h2 mobile:font-700 mobile:typo-h3">
          웰로에서 기부하면
          <br />
          받는 게 많아져요
        </p>
      </div>

      {/* 캐러셀 영역 */}
      <motion.div
        ref={emblaRef}
        initial={{ opacity: 0, y: 20 }}
        animate={{
          opacity: isReady ? 1 : 0,
          y: isReady ? 0 : 20,
        }}
        transition={{
          duration: 0.3,
          ease: 'easeOut',
        }}
        className="w-full overflow-hidden"
      >
        <div className="flex">
          {[...BANNER_IMAGES, ...BANNER_IMAGES].map((image, index) => (
            <div
              key={index}
              className="relative mr-66 aspect-square min-w-0 flex-[0_0_206px] mobile:mr-33 mobile:flex-[0_0_180px] tablet:mr-40 tablet:flex-[0_0_220px]"
            >
              <Image
                src={image.src}
                alt={image.alt}
                width={360}
                height={360}
                sizes="(max-width: 750px) 180px, 360px"
                className="size-full object-contain"
              />
            </div>
          ))}
        </div>
      </motion.div>

      {/* 세액공제 정보 */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{
          opacity: isReady ? 1 : 0,
          y: isReady ? 0 : 20,
        }}
        transition={{
          duration: 0.3,
          delay: 0.1,
          ease: 'easeOut',
        }}
        className="flex w-full flex-col items-center gap-32"
      >
        <div className="flex w-full flex-col items-center gap-24 pb-120 mobile:gap-12 mobile:pb-60">
          <div className="flex w-full items-center px-40 mobile:px-20">
            {TAX_DEDUCTION_INFO.map((info, index) => (
              <Fragment key={info.label}>
                {index > 0 && (
                  <Divider
                    variant="vertical"
                    className="mx-20 h-48 shrink-0 bg-Gray-300 mobile:mx-10 mobile:h-24"
                  />
                )}
                <div className="flex flex-1 flex-col items-center gap-2 text-center mobile:gap-0">
                  <p className="font-600 text-Gray-black typo-h5 mobile:typo-caption1">
                    {info.label}
                  </p>
                  <p className="font-700 text-O-500 typo-h4 mobile:typo-body4">
                    {info.rate}
                  </p>
                </div>
              </Fragment>
            ))}
          </div>
          <motion.div
            animate={{
              y: [0, 8, 0],
            }}
            transition={{
              duration: 0.8,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
            className="text-Gray-400"
          >
            <SvgChevrondown className="size-40 mobile:size-24" />
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

export default BannerSection;
